import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getUserName } from 'helpers';
import { Badge } from 'components/ui/badge';

const NOTE_PREVIEW_LENGTH = 240;
const frenchMonths = {
  janvier: 0,
  février: 1,
  mars: 2,
  avril: 3,
  mai: 4,
  juin: 5,
  juillet: 6,
  août: 7,
  septembre: 8,
  octobre: 9,
  novembre: 10,
  décembre: 11,
};

const parseVisitDate = (value) => {
  if (!value) return null;

  const isoDate = new Date(value);
  if (!Number.isNaN(isoDate.getTime())) return isoDate;

  const storedParts = value.match(/^(?:\S+\s+)?(\d{1,2})\s+(\S+)\s+(\d{4})\s+à\s+(\d{2}):(\d{2})(?::\d{2})?/u);
  if (!storedParts) return null;

  const [, day, monthName, year, hours, minutes] = storedParts;
  const month = frenchMonths[monthName.toLowerCase()];
  if (month === undefined) return null;

  return new Date(Number(year), month, Number(day), Number(hours), Number(minutes));
};

const formatVisitDate = (value) => {
  const date = parseVisitDate(value);
  if (!date) return value || 'Date non renseignée';

  const parts = new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value;
  return `${part('day')} ${part('month')} ${part('year')} · ${part('hour')}:${part('minute')}`;
};

const statusVariant = (status) => {
  if (status === 'Apte') return 'success';
  if (status === 'Inapte') return 'destructive';
  return 'pending';
};

const MedicalVisitHistory = ({ visits = [], layout = 'drawer' }) => {
  const [expandedNotes, setExpandedNotes] = useState({});
  const orderedVisits = [...visits].sort((left, right) => {
    const leftDate = parseVisitDate(left?.checkupDate);
    const rightDate = parseVisitDate(right?.checkupDate);
    if (!leftDate && !rightDate) return 0;
    if (!leftDate) return 1;
    if (!rightDate) return -1;
    return rightDate.getTime() - leftDate.getTime();
  });

  const isModalLayout = layout === 'modal';

  return (
    <div>
      {orderedVisits.map((visit, index) => {
        const note = typeof visit?.userNote === 'string' ? visit.userNote.trim() : '';
        const isLongNote = note.length > NOTE_PREVIEW_LENGTH;
        const noteKey = `${visit?.checkupDate || 'undated'}-${index}`;
        const isExpanded = !!expandedNotes[noteKey];
        const displayedNote = isLongNote && !isExpanded ? `${note.slice(0, NOTE_PREVIEW_LENGTH).trimEnd()}…` : note;
        const pilgrimName = getUserName(visit?.pilgrim?.first_name, visit?.pilgrim?.last_name);

        return (
          <article key={noteKey} className="border-b border-[#e7ece8] py-[17px] first:pt-[3px] last:border-b-0">
            <div className="mb-2.5 flex items-center justify-between gap-2.5">
              <time
                className="min-w-0 text-xs font-medium text-[#46594d]"
                dateTime={parseVisitDate(visit?.checkupDate)?.toISOString()}
              >
                {formatVisitDate(visit?.checkupDate)}
              </time>
              <Badge
                variant={statusVariant(visit?.checkupStatus)}
                className="shrink-0 rounded-[5px] px-[7px] py-0.5 text-[11px] font-medium"
              >
                {visit?.checkupStatus || 'En attente'}
              </Badge>
            </div>

            <dl
              className={
                isModalLayout
                  ? 'grid grid-cols-[1fr_1.2fr] gap-x-[18px] gap-y-3 text-[13px] leading-[1.55] max-sm:grid-cols-1 max-sm:gap-y-2.5'
                  : 'space-y-2.5 text-[13px] leading-[1.55]'
              }
            >
              <div
                className={
                  isModalLayout
                    ? 'min-w-0 max-sm:grid max-sm:grid-cols-[65px_minmax(0,1fr)] max-sm:gap-1.5'
                    : 'grid min-w-0 grid-cols-[65px_minmax(0,1fr)] gap-1.5'
                }
              >
                <dt
                  className={
                    isModalLayout
                      ? 'mb-[3px] text-[11px] text-[#79847e] max-sm:mb-0 max-sm:pt-0.5'
                      : 'pt-0.5 text-[11px] text-[#79847e]'
                  }
                >
                  Pèlerin
                </dt>
                <dd className="min-w-0 break-words text-[#2e4034]">{pilgrimName || 'Pèlerin non renseigné'}</dd>
              </div>
              <div
                className={
                  isModalLayout
                    ? 'min-w-0 max-sm:grid max-sm:grid-cols-[65px_minmax(0,1fr)] max-sm:gap-1.5'
                    : 'grid min-w-0 grid-cols-[65px_minmax(0,1fr)] gap-1.5'
                }
              >
                <dt
                  className={
                    isModalLayout
                      ? 'mb-[3px] text-[11px] text-[#79847e] max-sm:mb-0 max-sm:pt-0.5'
                      : 'pt-0.5 text-[11px] text-[#79847e]'
                  }
                >
                  Médecin
                </dt>
                <dd className="min-w-0 break-words text-[#2e4034]">
                  {visit?.doctorName || 'Médecin non renseigné'}
                  {visit?.doctorEmail && (
                    <span className="mt-0.5 block break-all text-[11px] leading-[1.55] text-[#7a857e]">
                      {visit.doctorEmail}
                    </span>
                  )}
                </dd>
              </div>
            </dl>

            <div className="mt-[13px]">
              <span className="mb-[3px] block text-[11px] text-[#79847e]">Notes</span>
              <p
                className={`whitespace-pre-wrap break-words text-xs leading-[1.65] ${
                  note ? 'text-[#53645a]' : 'text-[#8a938d]'
                }`}
              >
                {displayedNote || 'Aucune note'}
              </p>
              {isLongNote && (
                <button
                  type="button"
                  className="pt-1.5 text-xs leading-5 text-[#276e49] hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-expanded={isExpanded}
                  onClick={() => setExpandedNotes((current) => ({ ...current, [noteKey]: !current[noteKey] }))}
                >
                  {isExpanded ? 'Afficher moins' : 'Afficher plus'}
                </button>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
};

MedicalVisitHistory.propTypes = {
  visits: PropTypes.arrayOf(PropTypes.shape({})),
  layout: PropTypes.oneOf(['drawer', 'modal']),
};

export default MedicalVisitHistory;
